import React from 'react'

import { TextClamp } from '../../components/text/TextClamp'
import { priceDecimals, referencePriceDecimals } from '../../foundations/price-decimals'
import { clampArray } from '../../foundations/slide-limits'
import { PRICE_SERIES_ORDER, PRICE_SERIES_STYLE, PriceCurvesRole, fillTemplate } from './style'

import type { PriceCurvesPlotData, PriceCurvesPlotLabels } from './PriceCurvesPlot'
import type { StoredPlotPayload } from '../contract'

/**
 * The `price-curves` kind at slide size (plots wave 7, D2): the joint Van
 * Westendorp slide's tiles, chart and legend, moved here unchanged from
 * `PriceSensitivitySlide` so the slide draws its curves through the plot
 * library. The slide keeps its frame and its footer; geometry is frozen
 * (Stage 1), so every coordinate, size and colour below is the slide's own.
 *
 * GEOMETRY IS EXACT, LABELS ARE ROUNDED (D1). Every mark is positioned from the
 * exact crossings and `displayWindow`; every printed price reads `reported`,
 * at the payload's `decimals`. Below the thin base the producer withholds the
 * point and the view withholds it everywhere at once (D5).
 *
 * Words are the payload's label keys; a stored slide's adapter maps its
 * `displayLabels` onto them. `xAxisTitle` is the slide's own axis title (the
 * declared unit label), passed by the slide.
 */

export interface PriceCurvesSlideViewProps {
  data: PriceCurvesPlotData | StoredPlotPayload<'price-curves'>['data']
  /** The slide's axis title (declared unit, else currency, else the fallback label). */
  xAxisTitle?: string | null
  testIdPrefix?: string
}

export interface PriceSpan {
  low: number
  high: number
}

const GRAY = 'var(--slide-text-muted, #737373)'
const BLACK = 'var(--slide-text-primary, #000000)'
const EDGE = 'var(--slide-text-secondary, #384242)'
const GRID = 'var(--slide-border-soft, #E2E2E2)'
const BAND_FILL = 'var(--slide-neutral-mid, #D7D7D7)'
const REFERENCE = 'var(--slide-diverging-negative, #C2311F)'
const FAINT = 'var(--ds-text-faint, #A0AEC0)'

/** The band is annotation, never a fifth series, so it stays a wash. */
const BAND_FILL_OPACITY = 0.22

/**
 * Plot geometry in SVG user units. Four series that cross each other are only
 * separable by hue AND dash, so each role carries both: the falling pair reads
 * light to mid, the rising pair mid to dark, and the widths grow with severity.
 */
const PLOT = {
  left: 62,
  right: 1296,
  top: 52,
  // Shorter than the drawable width would allow (D1's marks still land on
  // exact crossings; only the axis's own vertical extent shrinks): the freed
  // space becomes breathing room between the 0% baseline and the tick /
  // edge-value labels below it, rather than sitting unused inside the plot.
  bottom: 315,
  width: 1320,
  height: 430,
}

const MARKER_TOP = 30
const TOP_LABEL_BASELINE = 18
const X_TICK_LABEL_BASELINE = 358
const EDGE_LABEL_BASELINE = 384
const EDGE_VALUE_BASELINE = 406
const TOP_LABEL_FONT_SIZE = 14
/** Half an average glyph advance at 14px, used only to keep labels in frame. */
const TOP_LABEL_HALF_CHAR = 3.6
const TOP_LABEL_MIN_GAP = 8

/** Candidate tick steps within a decade, ascending. */
const NICE_STEPS = [1, 2, 2.5, 5, 10]
const TARGET_TICK_INTERVALS = 6

/**
 * Where a price sits, horizontally. The ONE place a price becomes an x.
 *
 * Exported because the test that proves marks sit on the exact crossings has to
 * compute the expected position independently of the element it is checking.
 */
export function priceToX(price: number, view: PriceSpan): number {
  const span = view.high - view.low
  if (!Number.isFinite(span) || span <= 0) {
    return PLOT.left
  }
  return PLOT.left + ((price - view.low) / span) * (PLOT.right - PLOT.left)
}

/** Where a share sits, vertically. 0% on the baseline, 100% at the top rule. */
export function shareToY(share: number): number {
  const clamped = Math.max(0, Math.min(100, share))
  return PLOT.bottom - (clamped / 100) * (PLOT.bottom - PLOT.top)
}

/**
 * Round price ticks across the drawn window.
 *
 * Deliberately NOT the producer's reported-value rounding step: that step is
 * chosen for the width of the acceptable RANGE, and this axis spans the whole
 * drawn window, which is several times wider. One rule, two different inputs,
 * would put ticks at a spacing nobody picked for them.
 */
export function axisTicks(view: PriceSpan): Array<number> {
  const span = view.high - view.low
  if (!Number.isFinite(span) || span <= 0) {
    return []
  }
  const raw = span / TARGET_TICK_INTERVALS
  const decade = Math.pow(10, Math.floor(Math.log10(raw)))
  let step = NICE_STEPS[NICE_STEPS.length - 1] * decade
  for (const candidate of NICE_STEPS) {
    if (candidate * decade >= raw) {
      step = candidate * decade
      break
    }
  }
  const ticks: Array<number> = []
  const first = Math.ceil(view.low / step) * step
  for (let value = first; value <= view.high + step * 1e-9; value += step) {
    ticks.push(Number(value.toFixed(6)))
  }
  return ticks
}

/**
 * Keep the two top labels inside the frame and off each other.
 *
 * They are anchored to marks that can sit anywhere, including on top of one
 * another, and a label clipped at the frame edge or overprinted by its
 * neighbour is the collision the visual gate rejects. The MARKS never move;
 * only their labels do.
 */
export function layoutTopLabelX(
  labels: Array<{ x: number; halfWidth: number }>,
): Array<number> {
  const placed = labels.map((label) =>
    Math.max(
      PLOT.left + label.halfWidth,
      Math.min(PLOT.right - label.halfWidth, label.x),
    ),
  )
  if (placed.length !== 2) {
    return placed
  }
  const needed = labels[0].halfWidth + labels[1].halfWidth + TOP_LABEL_MIN_GAP
  const gap = Math.abs(placed[1] - placed[0])
  if (gap >= needed) {
    return placed
  }
  const centre = (placed[0] + placed[1]) / 2
  const leftFirst = placed[0] <= placed[1]
  const low = centre - needed / 2
  const high = centre + needed / 2
  const resolved = leftFirst ? [low, high] : [high, low]
  return resolved.map((value, index) =>
    Math.max(
      PLOT.left + labels[index].halfWidth,
      Math.min(PLOT.right - labels[index].halfWidth, value),
    ),
  )
}

function estimateHalfWidth(text: string): number {
  return text.length * TOP_LABEL_HALF_CHAR
}

/**
 * A price, as text. Unlabelled when the questionnaire captured no currency: a
 * "32" that silently means dollars to the reader and pounds to the respondent
 * is worse than a 32 with no symbol.
 */
export function formatPrice(
  value: number,
  currencyCode: string | null | undefined,
  decimals: number,
): string {
  if (!Number.isFinite(value)) {
    return ''
  }
  if (!currencyCode) {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: decimals,
      minimumFractionDigits: decimals,
    }).format(value)
  }
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      maximumFractionDigits: decimals,
      minimumFractionDigits: decimals,
    }).format(value)
  } catch {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: decimals,
      minimumFractionDigits: decimals,
    }).format(value)
  }
}

type SlideSeries = PriceCurvesPlotData['series'][number]

function seriesTestId(prefix: string, role: PriceCurvesRole): string {
  return `${prefix}-series-${role.replace(/_/g, '-')}`
}

function polylinePoints(series: SlideSeries, view: PriceSpan): string {
  return series.points
    .map((point) => `${priceToX(point.price, view).toFixed(2)},${shareToY(point.share).toFixed(2)}`)
    .join(' ')
}

export function PriceCurvesSlideView(props: PriceCurvesSlideViewProps) {
  const { data, xAxisTitle, testIdPrefix = 'price-sensitivity' } = props
  const labels: PriceCurvesPlotLabels = data.labels ?? {}
  const drawWindow = data.displayWindow
  const reported = data.reported
  const reference = data.reference ?? null
  const currencyCode = data.currencyCode ?? null

  const series = clampArray(data.series || [], 'price-sensitivity', 'series')
  const orderedSeries = PRICE_SERIES_ORDER.map((role) =>
    series.find((entry) => entry.role === role),
  ).filter((entry): entry is SlideSeries => Boolean(entry))

  // D5, once. `reported.point` absent means the optimum is absent from the
  // marker, the tile and every other surface at the same moment.
  const publishesPoint =
    typeof reported?.point === 'number' && typeof data.optimalPricePoint === 'number'

  const decimals = data.decimals
  const rangeLabel = labels.price_sensitivity_range_label ?? ''
  const optimumLabel = labels.price_sensitivity_optimum_label ?? ''
  const rangeText = `${formatPrice(reported.low, currencyCode, decimals)} ${labels.price_sensitivity_range_separator ?? ''} ${formatPrice(reported.high, currencyCode, decimals)}`
  const optimumText = publishesPoint
    ? `${optimumLabel} ${formatPrice(reported.point as number, currencyCode, decimals)}`
    : ''
  // D8b. The reference question's own display precision, which the producer
  // stamps on every new plot; a stored slide that predates it prints at the
  // slide's previous rule (legacy reader only).
  const referenceDecimals = reference
    ? referencePriceDecimals(reference.displayDecimals, priceDecimals([reference.value]))
    : 0
  const referenceBase = reference && labels.price_sensitivity_reference_base
    ? fillTemplate(labels.price_sensitivity_reference_base, { base: reference.baseSize })
    : ''
  const referenceText = reference
    ? `${labels.price_sensitivity_reference_label ?? ''} ${formatPrice(reference.value, currencyCode, referenceDecimals)} ${referenceBase}`.trim()
    : ''

  const bandX1 = priceToX(data.pointOfMarginalCheapness, drawWindow)
  const bandX2 = priceToX(data.pointOfMarginalExpensiveness, drawWindow)
  const optimumX = publishesPoint ? priceToX(data.optimalPricePoint as number, drawWindow) : null
  const referenceX = reference ? priceToX(reference.value, drawWindow) : null

  const topLabelInputs: Array<{ x: number; halfWidth: number }> = []
  if (optimumX != null) {
    topLabelInputs.push({ x: optimumX, halfWidth: estimateHalfWidth(optimumText) })
  }
  if (referenceX != null) {
    topLabelInputs.push({ x: referenceX, halfWidth: estimateHalfWidth(referenceText) })
  }
  const topLabelX = layoutTopLabelX(topLabelInputs)
  const optimumLabelX = optimumX != null ? topLabelX[0] : null
  const referenceLabelX =
    referenceX != null ? topLabelX[optimumX != null ? 1 : 0] : null

  const ticks = axisTicks(drawWindow)
  const gridShares = [0, 25, 50, 75, 100]
  const axisTitle = xAxisTitle || currencyCode || labels.price_sensitivity_x_axis_title_fallback || ''

  return (
    <>
      <div
        data-testid={`${testIdPrefix}-tiles`}
        style={{ display: 'flex', gap: 40, alignItems: 'flex-start', marginBottom: 14, flexShrink: 0 }}
      >
        <div data-testid={`${testIdPrefix}-tile-range`}>
          <TextClamp minLines={1} maxLines={1} lineHeight="1.2" style={{ fontSize: 13, color: GRAY }}>
            {rangeLabel}
          </TextClamp>
          <div
            data-testid={`${testIdPrefix}-tile-range-value`}
            style={{ fontSize: 30, fontWeight: 600, lineHeight: 1, color: BLACK, fontVariantNumeric: 'tabular-nums' }}
          >
            {rangeText}
          </div>
        </div>
        {publishesPoint ? null : (
          <div data-testid={`${testIdPrefix}-tile-point-withheld`}>
            <TextClamp minLines={1} maxLines={1} lineHeight="1.2" style={{ fontSize: 13, color: GRAY }}>
              {`*${optimumLabel}: ${labels.price_sensitivity_point_withheld ?? ''}`}
            </TextClamp>
          </div>
        )}
      </div>

      <div style={{ flex: 1, minHeight: 0 }}>
        <svg
          data-testid={`${testIdPrefix}-chart`}
          viewBox={`0 0 ${PLOT.width} ${PLOT.height}`}
          width="100%"
          role="img"
          aria-label={rangeLabel}
        >
          {gridShares.map((share) => (
            <React.Fragment key={share}>
              <line x1={PLOT.left} x2={PLOT.right} y1={shareToY(share)} y2={shareToY(share)} stroke={GRID} strokeWidth={1} />
              <text x={PLOT.left - 10} y={shareToY(share) + 4} textAnchor="end" fontSize={12} fill={GRAY}>
                {`${share}%`}
              </text>
            </React.Fragment>
          ))}
          <text x={PLOT.left} y={40} textAnchor="start" fontSize={12} fill={GRAY}>
            {labels.price_sensitivity_y_axis_title ?? ''}
          </text>

          <rect
            data-testid={`${testIdPrefix}-band`}
            x={bandX1}
            y={PLOT.top}
            width={Math.max(0, bandX2 - bandX1)}
            height={PLOT.bottom - PLOT.top}
            fill={BAND_FILL}
            fillOpacity={BAND_FILL_OPACITY}
          />
          <line data-testid={`${testIdPrefix}-band-edge-low`} x1={bandX1} x2={bandX1} y1={PLOT.top} y2={PLOT.bottom} stroke={EDGE} strokeWidth={1.5} />
          <line data-testid={`${testIdPrefix}-band-edge-high`} x1={bandX2} x2={bandX2} y1={PLOT.top} y2={PLOT.bottom} stroke={EDGE} strokeWidth={1.5} />

          {orderedSeries.map((entry) => {
            const style = PRICE_SERIES_STYLE[entry.role]
            return (
              <polyline
                key={entry.role}
                data-testid={seriesTestId(testIdPrefix, entry.role)}
                fill="none"
                stroke={style.stroke}
                strokeWidth={style.width}
                strokeDasharray={style.dash}
                points={polylinePoints(entry, drawWindow)}
              />
            )
          })}

          {optimumX != null ? (
            <>
              <line data-testid={`${testIdPrefix}-optimum-marker`} x1={optimumX} x2={optimumX} y1={MARKER_TOP} y2={PLOT.bottom} stroke={BLACK} strokeWidth={1.6} strokeDasharray="4 4" />
              <circle cx={optimumX} cy={PLOT.bottom} r={4} fill={BLACK} />
              <text
                data-testid={`${testIdPrefix}-optimum-label`}
                x={optimumLabelX as number}
                y={TOP_LABEL_BASELINE}
                textAnchor="middle"
                fontSize={TOP_LABEL_FONT_SIZE}
                fontWeight={600}
                fill={BLACK}
              >
                {optimumText}
              </text>
            </>
          ) : null}

          {referenceX != null ? (
            <>
              <line data-testid={`${testIdPrefix}-reference-marker`} x1={referenceX} x2={referenceX} y1={MARKER_TOP} y2={PLOT.bottom} stroke={REFERENCE} strokeWidth={2} strokeDasharray="2 5" />
              <text
                data-testid={`${testIdPrefix}-reference-label`}
                x={referenceLabelX as number}
                y={TOP_LABEL_BASELINE}
                textAnchor="middle"
                fontSize={TOP_LABEL_FONT_SIZE}
                fontWeight={600}
                fill={REFERENCE}
              >
                {referenceText}
              </text>
            </>
          ) : null}

          <line x1={PLOT.left} x2={PLOT.right} y1={PLOT.bottom} y2={PLOT.bottom} stroke={BAND_FILL} strokeWidth={1} />
          {ticks.map((tick) => (
            <React.Fragment key={tick}>
              <line x1={priceToX(tick, drawWindow)} x2={priceToX(tick, drawWindow)} y1={PLOT.bottom} y2={PLOT.bottom + 5} stroke={BAND_FILL} strokeWidth={1} />
              <text x={priceToX(tick, drawWindow)} y={X_TICK_LABEL_BASELINE} textAnchor="middle" fontSize={12} fill={GRAY}>
                {formatPrice(tick, currencyCode, priceDecimals([tick]))}
              </text>
            </React.Fragment>
          ))}

          <text x={bandX1} y={EDGE_LABEL_BASELINE} textAnchor="middle" fontSize={13} fill={GRAY}>
            {labels.price_sensitivity_lower_endpoint ?? ''}
          </text>
          <text
            data-testid={`${testIdPrefix}-edge-low-value`}
            x={bandX1}
            y={EDGE_VALUE_BASELINE}
            textAnchor="middle"
            fontSize={19}
            fontWeight={600}
            fill={BLACK}
          >
            {formatPrice(reported.low, currencyCode, decimals)}
          </text>
          <text x={bandX2} y={EDGE_LABEL_BASELINE} textAnchor="middle" fontSize={13} fill={GRAY}>
            {labels.price_sensitivity_upper_endpoint ?? ''}
          </text>
          <text
            data-testid={`${testIdPrefix}-edge-high-value`}
            x={bandX2}
            y={EDGE_VALUE_BASELINE}
            textAnchor="middle"
            fontSize={19}
            fontWeight={600}
            fill={BLACK}
          >
            {formatPrice(reported.high, currencyCode, decimals)}
          </text>
          <text x={PLOT.right} y={EDGE_VALUE_BASELINE} textAnchor="end" fontSize={12} fill={FAINT}>
            {axisTitle}
          </text>
        </svg>
      </div>

      <div
        data-testid={`${testIdPrefix}-legend`}
        style={{ display: 'flex', gap: 22, alignItems: 'center', marginTop: 10, flexShrink: 0 }}
      >
        {orderedSeries.map((entry) => {
          const style = PRICE_SERIES_STYLE[entry.role]
          return (
            <span
              key={entry.role}
              data-testid={`${testIdPrefix}-legend-item-${entry.role.replace(/_/g, '-')}`}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13, color: GRAY }}
            >
              <svg width={30} height={8} aria-hidden="true">
                <line x1={0} y1={4} x2={30} y2={4} stroke={style.stroke} strokeWidth={style.width} strokeDasharray={style.dash} />
              </svg>
              {labels[`price_role_${entry.role}` as const] ?? entry.label}
            </span>
          )
        })}
      </div>
    </>
  )
}
